<template>
  <button 
    @click="handleFulfillOrder"
    :disabled="disabled"
    class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-sm font-medium"
  >
    {{ t('fulfill') }}
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Order } from '@/models/sales/Order'
import { useSettingsStore } from '@/stores/settingsStore'
import { useRouter } from 'vue-router'
import { checkPurchaseOrdersReceived } from '@/utils/purchaseOrderHelpers'

export default defineComponent({
  name: 'FulfillButton',
  props: {
    order: {
      type: Order,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['statusChanged'],
  setup() {
    const router = useRouter()
    const { t } = useI18n({
      messages: {
        en: {
          fulfill: 'Fulfill',
          confirmAction: 'Confirm Action',
          warning: 'Warning',
          confirm: 'Confirm',
          cancel: 'Cancel',
          msgUpdated: 'Updated successfully',
          error: 'Error occurred',
          unreceivedPurchaseOrders: 'This order has unreceived special item(s). Please receive them first to continue.'
        },
        ar: {
          fulfill: 'تجهيز',
          confirmAction: 'هل انت متأكد؟',
          warning: 'تنبيه',
          confirm: 'تأكيد',
          cancel: 'إلغاء',
          msgUpdated: 'تم التحديث بنجاح',
          error: 'حدث خطأ',
          unreceivedPurchaseOrders: 'هذا الطلب يحتوي على مواد في طلبات الشراء غير مستلمة يرجى استلامها اولا للاستمرار'
        }
      }
    })

    return { t, router }
  },
  methods: {
    handleFulfillOrder() {
      // Check if all purchase orders have been received
      if (!checkPurchaseOrdersReceived(this.order.orderItems)) {
        ElMessage.error(this.t('unreceivedPurchaseOrders'))
        return
      }

      // If already processing, navigate directly to fulfill page
      if (this.order.orderStatus === 'processing') {
        this.router.push(this.$Route.SALES_ORDERS_ORDER_FULFILL.replace(':id', this.order.id as string))
        return
      }

      // Otherwise, change status to processing first
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        ElMessageBox.prompt(this.t('confirmAction'), this.t('warning'), {
          confirmButtonText: this.t('confirm'),
          cancelButtonText: this.t('cancel'),
          type: 'warning',
          inputType: 'password',
          inputPlaceholder: 'PIN Code'
        })
        .then(({ value }) => {
          this.submitStatusChange(value)
        })
        .catch(() => {
          console.log('cancelled')
        })
        return
      }

      ElMessageBox.confirm(this.t('confirmAction'), this.t('warning'), {
        confirmButtonText: this.t('confirm'),
        cancelButtonText: this.t('cancel'),
        type: 'warning'
      })
      .then(() => {
        this.submitStatusChange()
      })
      .catch(() => {
        console.log('cancelled')
      })
    },

    submitStatusChange(pin: string = "") {
      // Create a copy of the order with the new status
      const updatedOrder = { ...this.order }
      updatedOrder.orderStatus = 'processing'

      const headers = pin ? { pin: pin } : {}

      this.$http
        .post<Order>(
          `${Order.ENDPOINT}/change-order-status/${this.order.id}`,
          updatedOrder,
          { headers: headers }
        )
        .then((resp) => {
          ElMessage({
            type: 'success',
            message: this.t('msgUpdated')
          })
          
          // Emit event to notify parent component
          this.$emit('statusChanged', 'processing')
          
          // Navigate to fulfill page after successful status change
          this.router.push(this.$Route.SALES_ORDERS_ORDER_FULFILL.replace(':id', this.order.id as string))
        })
        .catch((error) => {
          ElMessage({
            type: 'error',
            message: this.t(error?.title || 'error')
          })
        })
    }
  }
})
</script> 